import React, { useState, useEffect } from 'react';
import { BsX } from 'react-icons/bs';
import { IoMdRadioButtonOn } from 'react-icons/io';
import { BsClock, BsGear, BsGraphUp, BsPencil, BsPencilSquare, BsCheck, BsEye, BsEyeSlash, BsChatDots, BsGrid3X3Gap } from 'react-icons/bs';
import './SettingsModal.css';
import VolumeKnob from './VolumeKnob';
import { auth } from '../firebase';
import { getUserProject, saveUserProject } from '../services/userService';
import { 
  getTotalSessionTime, 
  formatTimeHHMMSS, 
  updateSession, 
  getDailyFocusTimeData 
} from '../services/sessionService';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onManageSubscription: () => void;
  isLoadingManageSubscription: boolean;
  hasPaidSubscription: boolean;
  showProjectOverlay: boolean;
  onToggleProjectOverlay: (show: boolean) => void;
  enabledTools: { [key: string]: boolean };
  onToggleToolEnabled: (toolId: string, enabled: boolean) => void;
  minimalMode: boolean;
  onToggleMinimalMode: (enabled: boolean) => void;
}

// Define page types
type PageType = 'settings' | 'tools' | 'analytics';

// Custom formatter for the tooltip
const formatTooltipValue = (value: number) => {
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  
  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}m`;
  } else {
    result += `${Math.floor(value)}s`;
  }
  
  return result.trim();
};

// Format minutes on Y axis
const formatYAxis = (value: number) => {
  if (value === 0) return '0';
  return `${Math.floor(value / 60)}m`;
};

const SettingsModal: React.FC<SettingsModalProps> = ({ 
  isOpen, 
  onClose, 
  volume, 
  onVolumeChange,
  onManageSubscription,
  isLoadingManageSubscription,
  hasPaidSubscription,
  showProjectOverlay,
  onToggleProjectOverlay,
  enabledTools,
  onToggleToolEnabled,
  minimalMode,
  onToggleMinimalMode
}) => {
  const [totalSessionTime, setTotalSessionTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dailyFocusData, setDailyFocusData] = useState<Array<{day: string; value: number}>>([]);
  const [activePage, setActivePage] = useState<PageType>('settings');
  const [projectDescription, setProjectDescription] = useState<string>('');
  const [isEditingProject, setIsEditingProject] = useState<boolean>(false);
  const [editedProjectDescription, setEditedProjectDescription] = useState<string>('');
  const [isSavingProject, setIsSavingProject] = useState<boolean>(false);

  // Load user data when the modal opens
  useEffect(() => {
    if (isOpen) {
      // Update the current session first to ensure it has the latest data
      updateSession().then(() => {
        // Small delay to ensure Firestore has propagated the update
        setTimeout(() => {
          loadTotalSessionTime();
          loadDailyFocusData();
          loadProjectDescription();
        }, 300);
      }).catch(error => {
        console.error('Error updating session before loading total time:', error);
        // Still try to load the data even if the update fails
        loadTotalSessionTime();
        loadDailyFocusData();
        loadProjectDescription();
      });
    }
  }, [isOpen]);

  // Function to load the total session time
  const loadTotalSessionTime = async () => {
    setIsLoading(true);
    try {
      const totalTime = await getTotalSessionTime();
      setTotalSessionTime(totalTime);
    } catch (error) {
      console.error('Error loading total session time:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to load daily focus time data
  const loadDailyFocusData = async () => {
    try {
      const data = await getDailyFocusTimeData();
      setDailyFocusData(data);
    } catch (error) {
      console.error('Error loading daily focus data:', error);
    }
  };

  // Function to load the project description
  const loadProjectDescription = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const description = await getUserProject(currentUser.uid);
        if (description) {
          setProjectDescription(description);
          setEditedProjectDescription(description);
        }
      }
    } catch (error) {
      console.error('Error loading project description:', error);
    }
  };

  // Function to save the project description
  const handleSaveProject = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser && editedProjectDescription.trim()) {
        setIsSavingProject(true);
        await saveUserProject(currentUser.uid, editedProjectDescription.trim());
        setProjectDescription(editedProjectDescription.trim());
        setIsEditingProject(false);
        // Also update localStorage
        localStorage.setItem('vibeUserProject', editedProjectDescription.trim());
      }
    } catch (error) {
      console.error('Error saving project description:', error);
    } finally {
      setIsSavingProject(false);
    }
  };
  
  if (!isOpen) return null;

  // Convert volume from 0-1 scale to 0-100 for the knob
  const volumePercentage = volume * 100;
  
  // Handle volume change from knob (0-100) and convert back to 0-1 scale
  const handleVolumeChange = (newVolume: number) => {
    onVolumeChange(newVolume / 100);
  };

  // Custom icon with a radial, symmetrical design
  const VolumeIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="4" fill="rgba(255, 255, 255, 0.7)" />
      <circle 
        cx="12" 
        cy="12" 
        r="7" 
        stroke="rgba(255, 255, 255, 0.5)" 
        strokeWidth="1.5" 
        fill="none" 
      />
      <circle 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="rgba(255, 255, 255, 0.3)" 
        strokeWidth="1" 
        fill="none" 
      />
    </svg>
  );

  // Settings page content
  const renderSettingsPage = () => (
    <>
      <div className="settings-section project-section">
        <div className="section-header-with-actions">
          <h3>Your Project</h3>
          <div className="project-actions">
            <div 
              className="project-visibility-toggle"
              onClick={() => onToggleProjectOverlay(!showProjectOverlay)}
              title={showProjectOverlay ? "Hide project on main page" : "Show project on main page"}
            >
              {showProjectOverlay ? <BsEye /> : <BsEyeSlash />}
            </div>
            {!isEditingProject ? (
              <button 
                className="icon-button" 
                onClick={() => setIsEditingProject(true)}
                title="Edit Project"
              >
                <BsPencil />
              </button>
            ) : (
              <button 
                className="icon-button" 
                onClick={handleSaveProject}
                disabled={isSavingProject}
                title="Save Project"
              >
                <BsCheck />
              </button>
            )}
          </div>
        </div>
        
        {isEditingProject ? (
          <div className="project-edit-container">
            <textarea 
              className="project-description-editor"
              value={editedProjectDescription}
              onChange={(e) => setEditedProjectDescription(e.target.value)}
              placeholder="What are you working on?"
              disabled={isSavingProject}
            />
          </div>
        ) : (
          <div className="project-description">
            {projectDescription || "..."}
          </div>
        )}
      </div>

      <div className="interface-toggle-section">
        <div className="interface-toggles">
          <div className="interface-toggle">
            <div className="interface-toggle-info">
              <div className="interface-toggle-text">
                <span className="interface-toggle-name">Minimal Mode</span>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={minimalMode}
                onChange={(e) => onToggleMinimalMode(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Volume</h3>
        <VolumeKnob
          value={volumePercentage}
          onChange={handleVolumeChange}
          icon={<VolumeIcon />}
        />
      </div>
      
      {hasPaidSubscription && (
        <div className="settings-section">
          <button 
            className="subscription-button"
            onClick={onManageSubscription}
            disabled={isLoadingManageSubscription}
          >
            {isLoadingManageSubscription ? 'Loading...' : 'Manage Subscription'}
          </button>
        </div>
      )}
    </>
  );

  // Tools page content
  const renderToolsPage = () => (
    <>
      <div className="settings-section tools-panel-section">
        <div className="tools-panel-grid">
          <div className="tool-panel-item">
            <div className="tool-panel-card">
              <div className="tool-panel-icon">
                <BsPencil />
              </div>
              <div className="tool-panel-content">
                <h4>Notes</h4>
                <p>Notepad with AI assistance</p>
              </div>
              <div className="tool-panel-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={enabledTools.notes || false}
                    onChange={(e) => onToggleToolEnabled('notes', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
          
          <div className="tool-panel-item">
            <div className="tool-panel-card">
              <div className="tool-panel-icon">
                <BsChatDots />
              </div>
              <div className="tool-panel-content">
                <h4>Chat</h4>
                <p>Connect with other creators</p>
              </div>
              <div className="tool-panel-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={enabledTools.chat || false}
                    onChange={(e) => onToggleToolEnabled('chat', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Analytics page content
  const renderAnalyticsPage = () => (
    <>
      <div className="settings-section focus-time-section">
        <h3>Total Focus Time</h3>
        <div className="total-time-display">
          <div className="total-time-counter">
            {formatTimeHHMMSS(totalSessionTime)}
          </div>
        </div>
        
        <div className="focus-chart-container">
          <h4 className="chart-title">Past 7 Days</h4>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={dailyFocusData}
              margin={{
                top: 20,
                right: 20,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.08)" />
              <XAxis 
                dataKey="day" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              />
              <YAxis 
                tickFormatter={formatYAxis}
                axisLine={false} 
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }}
              />
              <Tooltip 
                formatter={(value: number) => [formatTooltipValue(value), 'Focus Time']}
                contentStyle={{ 
                  backgroundColor: 'rgba(20,20,20,0.9)', 
                  border: 'none', 
                  borderRadius: '4px', 
                  color: 'white' 
                }}
              />
              <Bar 
                dataKey="value" 
                fill="rgba(255, 255, 255, 0.75)" 
                radius={[4, 4, 0, 0]}
                animationDuration={1000}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );

  return (
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          <BsX />
        </button>
        
        {/* <div className="settings-header">
          <h2>{activePage === 'settings' ? 'Settings' : 'Analytics'}</h2>
        </div> */}

        <div className="page-toggle">
          <button 
            className={`toggle-button ${activePage === 'settings' ? 'active' : ''}`} 
            onClick={() => setActivePage('settings')}
          >
            <BsGear />
            <span>Settings</span>
          </button>
          <button 
            className={`toggle-button ${activePage === 'tools' ? 'active' : ''}`}
            onClick={() => setActivePage('tools')}
          >
            <BsGrid3X3Gap />
            <span>Tools</span>
          </button>
          <button 
            className={`toggle-button ${activePage === 'analytics' ? 'active' : ''}`}
            onClick={() => setActivePage('analytics')}
          >
            <BsGraphUp />
            <span>Analytics</span>
          </button>
        </div>
        
        <div className="settings-content">
          {activePage === 'settings' ? renderSettingsPage() : activePage === 'tools' ? renderToolsPage() : renderAnalyticsPage()}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal; 